/**
 * Detail panel for a single selected matchday: how each member's points changed and
 * the match results that drove it.
 *
 * Per-user gains and rank movements are derived from the league history columns
 * (helpers/standingsSeries.ts); match results are the session gameResults filtered to
 * this matchday (gameResult shape in models/gameUpdate.ts).
 */
import { Box, Divider, Flex, HStack, Icon, Text, useColorMode, useColorModeValue, VStack } from '@chakra-ui/react';
import { FaArrowDown, FaArrowUp, FaMinus } from 'react-icons/fa';

import { Flag } from '@/components/Flag';
import { matchdayDelta, rankAt, type UserSeries } from '@/helpers/leagues/standingsSeries';
import { countriesInfo } from '@/models/country';
import type { gameResult } from '@/models/gameUpdate';
import type { LeagueHistoryColumn } from '@/models/leagueHistory';
import { chartColor } from './chartPalette';

export interface MatchdayDetailCardProps {
  matchday: number;
  series: UserSeries[];
  columns: LeagueHistoryColumn[];
  colorIndex: Map<string, number>;
  currentUid?: string;
  /** All deduped game results (any matchday); filtered to this matchday internally. */
  gameResults: gameResult[];
  /** When true, omit outer card chrome (content sits inside a profile-style card). */
  embedded?: boolean;
}

interface MovementRow {
  uid: string;
  name: string;
  delta: number | null;
  rankDelta: number | null; // positive = climbed
  isCurrent: boolean;
}

function RankMovement({ rankDelta }: { rankDelta: number | null }) {
  if (rankDelta == null || rankDelta === 0) {
    return (
      <HStack spacing={0.5} color='gray.500'>
        <Icon as={FaMinus} boxSize={2.5} />
      </HStack>
    );
  }
  const climbed = rankDelta > 0;
  return (
    <HStack spacing={0.5} color={climbed ? 'green.500' : 'red.400'}>
      <Icon as={climbed ? FaArrowUp : FaArrowDown} boxSize={2.5} />
      <Text fontSize='2xs' fontWeight={700}>
        {Math.abs(rankDelta)}
      </Text>
    </HStack>
  );
}

export function MatchdayDetailCard(props: MatchdayDetailCardProps) {
  const { colorMode } = useColorMode();
  const cardBg = useColorModeValue('surfaceCard', 'surfaceCard');
  const border = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  const embedded = props.embedded ?? false;

  const k = props.matchday;

  const movements: MovementRow[] = props.series
    .map((s) => {
      const currRank = rankAt(props.columns, k, s.uid);
      const prevRank = rankAt(props.columns, k - 1, s.uid);
      const rankDelta = currRank != null && prevRank != null ? prevRank - currRank : null;
      return {
        uid: s.uid,
        name: s.name,
        delta: matchdayDelta(s, k),
        rankDelta,
        isCurrent: props.currentUid === s.uid,
      };
    })
    .filter((row) => row.delta != null)
    .sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0));

  const results = props.gameResults
    .filter((r) => r.matchday === k)
    .sort((a, b) => b.pointsA + b.pointsB - (a.pointsA + a.pointsB));

  const body = (
    <>
      <Text fontSize='sm' fontWeight={700} mb={3}>
        MatchDay {k}
      </Text>

      <Flex direction={{ base: 'column', md: 'row' }} gap={4} align='stretch'>
        {/* Points gained per member */}
        <VStack align='stretch' spacing={1} flex='1' minW={0}>
          <Text fontSize='xs' color='gray.500' fontWeight={600}>
            Points this matchday
          </Text>
          {movements.length === 0 ? (
            <Text fontSize='xs' color='gray.500'>
              No standings recorded for this matchday yet.
            </Text>
          ) : (
            movements.map((row) => {
              const idx = props.colorIndex.get(row.uid) ?? 0;
              return (
                <HStack key={row.uid} spacing={2} justify='space-between'>
                  <HStack spacing={2} minW={0}>
                    <Box w='10px' h='10px' borderRadius='full' bg={chartColor(idx, colorMode)} flexShrink={0} />
                    <Text fontSize='xs' fontWeight={row.isCurrent ? 700 : 500} noOfLines={1}>
                      {row.name}
                    </Text>
                  </HStack>
                  <HStack spacing={2} flexShrink={0}>
                    <RankMovement rankDelta={row.rankDelta} />
                    <Text
                      fontSize='xs'
                      fontWeight={700}
                      color={(row.delta ?? 0) > 0 ? 'green.500' : 'gray.500'}
                      minW='34px'
                      textAlign='right'
                    >
                      +{row.delta ?? 0}
                    </Text>
                  </HStack>
                </HStack>
              );
            })
          )}
        </VStack>

        <Divider orientation='vertical' display={{ base: 'none', md: 'block' }} />

        {/* Match results that drove the changes */}
        <VStack align='stretch' spacing={1.5} flex='1' minW={0}>
          <Text fontSize='xs' color='gray.500' fontWeight={600}>
            Results
          </Text>
          {results.length === 0 ? (
            <Text fontSize='xs' color='gray.500'>
              No match results recorded for this matchday.
            </Text>
          ) : (
            results.map((r, i) => (
              <HStack key={`${r.countryA}-${r.countryB}-${i}`} spacing={2} justify='space-between'>
                <HStack spacing={1.5} minW={0}>
                  <Flag country={r.countryA} w={4} />
                  <Text fontSize='xs' noOfLines={1}>
                    {countriesInfo[r.countryA]?.name ?? r.countryA}
                  </Text>
                  <Text fontSize='xs' fontWeight={700}>
                    {r.goalsA}–{r.goalsB}
                  </Text>
                  <Text fontSize='xs' noOfLines={1}>
                    {countriesInfo[r.countryB]?.name ?? r.countryB}
                  </Text>
                  <Flag country={r.countryB} w={4} />
                </HStack>
                <Text fontSize='2xs' color='gray.500' flexShrink={0}>
                  +{r.pointsA}/+{r.pointsB}
                </Text>
              </HStack>
            ))
          )}
        </VStack>
      </Flex>
    </>
  );

  if (embedded) {
    return (
      <Box w='full' h='full' display='flex' flexDirection='column'>
        {body}
      </Box>
    );
  }

  return (
    <Box bg={cardBg} borderWidth='1px' borderColor={border} borderRadius='lg' p={4}>
      {body}
    </Box>
  );
}
